import { FC, useState } from "react";
import { DateTime } from "luxon";
import {
  AmountCellProps,
  AnyUserCellProps,
  CreateEntityTableCellProps,
  CustomerCellProps,
  DateTableCellProps,
  HiddenDataTableCellProps,
} from "./TableCells.interface";
import {
  CreateEntities,
  ExternalIntegration,
  ExternalIntegrationTypes,
} from "@fena/toolkit-types";
import styled from "styled-components";
import { Avatar } from "../avatar/Avatar";
import { AvatarSizeVariant } from "../avatar/Avatar.interface";
import { Typography } from "../typography/Typography";
import { TypographyVariant } from "../typography/Typography.interface";
import { Button, ButtonVariant } from "../button";
import FaireLogo from "../../icons/FaireLogo";
import {
  Opencart,
  ShopifyIcon,
  WoocommerceIcon,
  ZapierNewLogo,
} from "../../icons";
import { formatCurrency } from "../../../util/data-utils";

export const DateTableCell: FC<DateTableCellProps> = ({
  date,
  format = "dd LLL, yyyy 'at' h:mm a",
  className,
}) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const localized = date
    ? DateTime.fromISO(date, { zone: userTimeZone }).toFormat(format)
    : "-";

  return <div className={className}>{localized}</div>;
};

const CreatedByWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TypographyWrapper = styled(Typography)`
  margin-left: 10px;
`;

const Clickable = styled.div`
  display: flex;
`;

const ImageBg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #fff;
  width: 38px;
  height: 38px;
`;

const StyledFaireLogo = styled(FaireLogo)`
  transform: scale(0.9);
`;

const CustomerCellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const CreateEntityTableCell: FC<CreateEntityTableCellProps> = ({
  createdByEntity,
  createdBy,
  className,
}) => {
  if (!createdBy) return null;
  if (createdByEntity === CreateEntities.USER) {
    return (
      <CreatedByWrapper className={className}>
        <Avatar
          disableDropdown
          size={AvatarSizeVariant.SMALL}
          avatarSrc={"avatar" in createdBy ? createdBy.avatar?.url : undefined}
        />
        <TypographyWrapper variant={TypographyVariant.BODY2}>
          {"firstName" in createdBy ? createdBy.firstName : ""}{" "}
          {"lastName" in createdBy ? createdBy.lastName : ""}
        </TypographyWrapper>
      </CreatedByWrapper>
    );
  } else if (createdByEntity === CreateEntities.EXTERNAL_INTEGRATION) {
    return (
      <CreatedByWrapper>
        <ImageBg>
          {(createdBy as ExternalIntegration)?.type ===
            ExternalIntegrationTypes.WOOCOMMERCE && <WoocommerceIcon />}
          {(createdBy as ExternalIntegration)?.type ===
            ExternalIntegrationTypes.ZAPIER && <ZapierNewLogo />}
          {(createdBy as ExternalIntegration)?.type ===
            ExternalIntegrationTypes.API2CART && <Opencart />}
          {(createdBy as ExternalIntegration)?.type ===
            ExternalIntegrationTypes.SHOPIFY && <ShopifyIcon />}
          {(createdBy as ExternalIntegration)?.type ===
            ExternalIntegrationTypes.FAIRE && <StyledFaireLogo />}
        </ImageBg>

        <TypographyWrapper variant={TypographyVariant.BODY2}>
          {"name" in createdBy ? createdBy.name : ""}
        </TypographyWrapper>
      </CreatedByWrapper>
    );
  } else if (createdByEntity === CreateEntities.SYSTEM) {
    return (
      <CreatedByWrapper className={className}>
        <TypographyWrapper variant={TypographyVariant.BODY2}>
          {"name" in createdBy ? createdBy.name : ""}{" "}
        </TypographyWrapper>
      </CreatedByWrapper>
    );
  } else {
    return (
      <CreatedByWrapper className={className}>
        <TypographyWrapper variant={TypographyVariant.BODY2}>
          {"name" in createdBy ? createdBy.name : ""}{" "}
        </TypographyWrapper>
      </CreatedByWrapper>
    );
  }
};

export const HiddenDataTableCell: FC<HiddenDataTableCellProps> = ({ data }) => {
  const [show, setShow] = useState(false);

  const onShow = () => {
    setShow(!show);
  };

  return show ? (
    <Clickable onClick={onShow}>{data}</Clickable>
  ) : (
    <Button variant={ButtonVariant.OUTLINED} onClick={onShow}>
      Show
    </Button>
  );
};

const MemberContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0;
`;

const Box = styled.div`
  margin-left: 12px;
  display: flex;
  flex-direction: column;
`;

const Typo = styled(Typography)`
  line-height: 18px;
`;

export const AnyUserCell: FC<AnyUserCellProps> = ({
  email,
  name,
  avatarUrl,
  companyName,
  className,
}) => {
  return (
    <MemberContainer className={className}>
      <Avatar
        avatarSrc={avatarUrl}
        size={AvatarSizeVariant.MEDIUM}
        disableDropdown
      />
      <Box>
        <Typo variant={TypographyVariant.BODY4}>{name}</Typo>
        <Typo variant={TypographyVariant.BODY7} color="#8181a5">
          {email}
        </Typo>
        <Typo variant={TypographyVariant.BODY7}>{companyName}</Typo>
      </Box>
    </MemberContainer>
  );
};

export const AmountCell: FC<AmountCellProps> = ({
  amount,
  direction,
  currency,
}) => {
  const amountValue = formatCurrency(amount, 2, currency);
  return (
    <Typography
      color={direction === "incoming" ? "#00bc82" : "#ef6355"}
      variant={TypographyVariant.BODY4}
    >
      {direction === "outgoing" ? `-${amountValue}` : amountValue}
    </Typography>
  );
};

export const CustomerCell: FC<CustomerCellProps> = ({
  customerName,
  customerEmail,
}) => {
  if (!customerName && !customerEmail) {
    return <>-</>;
  }
  return (
    <CustomerCellWrapper>
      <Typography variant={TypographyVariant.BODY3}>{customerName}</Typography>
      <Typography variant={TypographyVariant.BODY7} color="#8181a5">
        {customerEmail}
      </Typography>
    </CustomerCellWrapper>
  );
};
